import {
  SpartenType,
  TaasTarifrisikoschluesselTHH,
  TaasThhPferdData,
  Tiergroesse,
} from '@ps20/bff-models';
import { convertThhTaasBausteinToTarifrisikoschluessel } from '@ps20-frontend/frontend-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import { IAppState } from '../../appState';
import { AnzahlTiereEnum, Stockmass } from '../../reducers/versicherungsnehmer.reducer';
import { selectIsNeugeschaeft } from '../../../app/shared/selectors/vertragsoptionen.selector';

export function buildTaasThhPferdDataForStrecke(state: IAppState): TaasThhPferdData {
  const thhData: TaasThhPferdData = generateBasicTaasThhPferdData(state);
  thhData.salesProducts = buildBasicSalesProductsForStrecke(
    state.produkt,
    convertThhTaasBausteinToTarifrisikoschluessel,
  );
  if (!selectIsNeugeschaeft(state)) {
    enhanceTierbausteine(thhData, state);
  }
  return thhData;
}

export function buildTaasThhPferdDataForShop(state: IAppState): TaasThhPferdData {
  const thhData: TaasThhPferdData = generateBasicTaasThhPferdData(state);
  thhData.salesProducts = buildBasicSalesProductsForShop(
    state.produkt,
    convertThhTaasBausteinToTarifrisikoschluessel,
  );
  if (!selectIsNeugeschaeft(state)) {
    enhanceTierbausteine(thhData, state);
  }
  return thhData;
}

function generateBasicTaasThhPferdData(state: IAppState): TaasThhPferdData {
  const hasOnlyOneHorse: boolean = state.versicherungsnehmer.anzahlTiere === AnzahlTiereEnum.EINES;
  return {
    spartenType: SpartenType.TIERHALTERHAFTPFLICHT_PFERD,
    anzVorschaedenPferd: state.vorschaeden.thhPferdVorschaeden,
    tiergroesseList: hasOnlyOneHorse
      ? [convertStockmassToTiergroesse(state.versicherungsnehmer.stockmass)]
      : state.versicherungsnehmer.stockmassMehrerePferde.map(value =>
          convertStockmassToTiergroesse(value),
        ),
    plzVN: state.risikoort.plz,
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    salesProducts: null,
    urbeginndatum: state.vertragsoptionen?.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
  };
}

export function convertStockmassToTiergroesse(stockmass: Stockmass): Tiergroesse {
  return {
    [Stockmass.GROESSER]: Tiergroesse.GROSSPFERD,
    [Stockmass.KLEINER]: Tiergroesse.KLEINPFERD,
  }[stockmass];
}

function enhanceTierbausteine(thhData: TaasThhPferdData, state: IAppState): void {
  thhData.tierBausteine = [];
  const deltafaktoren = state.taasInfo.deltafaktoren;
  const bausteinAnpassungsfaktoren = state.taasInfo.bausteinAnpassungsfaktoren;
  thhData.plzFaktor = state.taasInfo.deltafaktoren?.plzFaktor;
  if (state.versicherungsnehmer.tiere) {
    state.versicherungsnehmer.tiere?.forEach(tier => {
      thhData.tierBausteine.push({
        kennung: TaasTarifrisikoschluesselTHH.P + tier.instanzId,
        instanzId: tier.instanzId,
        tarifrisikoschluessel: TaasTarifrisikoschluesselTHH.P,
        tierRasse: tier.tierrasse,
        tierGroesse: tier.tiergroesse,
        deltafaktor: deltafaktoren?.deltafaktoren?.find(
          faktor => faktor.instanzId === tier.instanzId,
        )?.faktor,
        bausteinAnpassungsfaktor: bausteinAnpassungsfaktoren?.bausteinAnpassungsfaktoren?.find(
          faktor => faktor.instanzId === tier.instanzId,
        )?.bausteinAnpassungsfaktor,
      });
    });
    if (
      state.versicherungsnehmer.tiere.length !==
      state.versicherungsnehmer.stockmassMehrerePferde?.filter(stockmass => !!stockmass).length
    ) {
      addMissingTierBausteineWithoutDeltafaktor(state, thhData);
    }
  } else {
    const tierTicker = thhData.tiergroesseList.map(s => s);
    tierTicker?.forEach((value, index) => {
      thhData.tierBausteine.push({
        instanzId: index + 1,
        tarifrisikoschluessel: TaasTarifrisikoschluesselTHH.P,
        tierGroesse: value,
      });
    });
  }
  fixInstanzIds(thhData);
}

export function addMissingTierBausteineWithoutDeltafaktor(
  state: IAppState,
  thhData: TaasThhPferdData,
): void {
  let missingStockmasses = state.versicherungsnehmer.stockmassMehrerePferde
    ? [...state.versicherungsnehmer.stockmassMehrerePferde]
    : [];
  missingStockmasses = missingStockmasses.concat(state.versicherungsnehmer.stockmass);
  const bausteineStockmass = thhData.tierBausteine.map(tierBaustein =>
    tierBaustein.tierGroesse === Tiergroesse.GROSSPFERD ? Stockmass.GROESSER : Stockmass.KLEINER,
  );
  bausteineStockmass.forEach(bausteinStockmass => {
    const index = missingStockmasses.indexOf(bausteinStockmass);
    if (index !== -1) {
      missingStockmasses.splice(index, 1);
    }
  });
  missingStockmasses = missingStockmasses.filter(stockmass => !!stockmass);
  const maxExistingInstanzId = getMaxExistingInstanzId(thhData);
  missingStockmasses.forEach((stockmass, index) => {
    const instanzId = maxExistingInstanzId + index + 1;
    thhData.tierBausteine.push({
      instanzId,
      tarifrisikoschluessel: TaasTarifrisikoschluesselTHH.P,
      tierGroesse: convertStockmassToTiergroesse(stockmass),
      kennung: TaasTarifrisikoschluesselTHH.P + instanzId,
    });
  });
  // if one or more of horses deleted
  const tiergroesseList = thhData.tiergroesseList;
  thhData.tierBausteine.forEach((tierbaustein, index) => {
    const countTiergroesseList = tiergroesseList.filter(
      tiergroesse => tiergroesse === tierbaustein.tierGroesse,
    ).length;
    const countTiergroesseBausteine = thhData.tierBausteine.filter(
      tiergroesse => tiergroesse.tierGroesse === tierbaustein.tierGroesse,
    ).length;
    if (countTiergroesseList < countTiergroesseBausteine) {
      thhData.tierBausteine.splice(index, 1);
    }
  });
  thhData.tierBausteine = thhData.tierBausteine.filter(tierbaustein => !!tierbaustein);
}

export function fixInstanzIds(taasThhData: TaasThhPferdData): void {
  const maxExistingInstanzId = getMaxExistingInstanzId(taasThhData);
  taasThhData.tierBausteine?.forEach((tierbaustein, index) => {
    if (!tierbaustein.instanzId) {
      const instanzId = maxExistingInstanzId + index + 1;
      tierbaustein.instanzId = instanzId;
      tierbaustein.kennung = TaasTarifrisikoschluesselTHH.P + instanzId;
    }
  });
}

function getMaxExistingInstanzId(taasThhData: TaasThhPferdData): number {
  const result = taasThhData.tierBausteine.reduce((instanzId, tierBaustein) => {
    return Math.max(instanzId, tierBaustein.instanzId);
  }, 0);
  return result || 0;
}
